import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Search,
  Users,
  CheckCircle,
  Send,
  MoreVertical,
  Trash2,
  Edit,
  Loader2,
  Mail,
  AlertCircle
} from "lucide-react";
import {
  fetchMailingLists,
  deleteMailingList,
  formatRelativeTime,
  DATA_SOURCES
} from "../../services/MailingListService";
import CreateListModal from "./CreateListModal";

/**
 * MailingListsView - Grid of mailing list cards with search and create
 */
const MailingListsView = ({ onSelectList, userEmail, userMeta }) => {
  const [lists, setLists] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingList, setEditingList] = useState(null);
  const [deletingListId, setDeletingListId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  // Load mailing lists
  const loadLists = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetchMailingLists();
      setLists(response.lists || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  // Handle delete
  const handleDelete = async (listId) => {
    if (!window.confirm("Are you sure you want to delete this mailing list? This action cannot be undone.")) {
      return;
    }

    try {
      setDeletingListId(listId);
      await deleteMailingList(listId);
      setLists((prev) => prev.filter((l) => l.id !== listId));
    } catch (err) {
      alert(err.message);
    } finally {
      setDeletingListId(null);
      setOpenMenuId(null);
    }
  };

  // Filter lists by search term
  const filteredLists = lists.filter((list) =>
    list.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (list.description || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Close menu when clicking outside
  useEffect(() => {
    if (!openMenuId) return;

    const handleClickOutside = () => {
      setOpenMenuId(null);
    };

    // Add listener on next tick to avoid capturing the opening click
    const timer = setTimeout(() => {
      document.addEventListener("click", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [openMenuId]);

  // Handle card click
  const handleCardClick = (list) => {
    console.log('[MailingListsView] Card clicked, list:', list);
    if (deletingListId !== list.id) {
      onSelectList(list);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 text-sky-600 animate-spin" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold dark:text-white text-slate-900 mb-2">
            Failed to load mailing lists
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-4">{error}</p>
          <button
            onClick={loadLists}
            className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-500 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-lg font-semibold dark:text-white text-slate-900">
            Mailing Lists
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {lists.length} {lists.length === 1 ? "list" : "lists"}
          </p>
        </div>
        <button
          onClick={() => {
            setEditingList(null);
            setShowCreateModal(true);
          }}
          className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg font-medium flex items-center gap-2 transition-colors shadow-lg shadow-sky-900/20"
        >
          <Plus className="w-4 h-4" />
          New List
        </button>
      </div>

      {/* Search */}
      {lists.length > 0 && (
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search lists..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
          />
        </div>
      )}

      {/* Empty state */}
      {lists.length === 0 && (
        <div className="text-center py-16">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
            <Mail className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-2">
            No mailing lists yet
          </h3>
          <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-sm mx-auto">
            Create your first mailing list to start managing contacts and sending postcards.
          </p>
          <button
            onClick={() => {
              setEditingList(null);
              setShowCreateModal(true);
            }}
            className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg font-medium inline-flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Mailing List
          </button>
        </div>
      )}

      {/* No results */}
      {lists.length > 0 && filteredLists.length === 0 && (
        <div className="text-center py-16">
          <Search className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-2">
            No lists found
          </h3>
          <p className="text-slate-500 dark:text-slate-400">
            No mailing lists match "{searchTerm}"
          </p>
        </div>
      )}

      {/* List cards grid */}
      {filteredLists.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredLists.map((list) => {
            const isDeleting = deletingListId === list.id;
            const isMenuOpen = openMenuId === list.id;

            return (
              <div
                key={list.id}
                className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 hover:border-sky-300 dark:hover:border-sky-700 transition-all cursor-pointer group ${
                  isDeleting ? "opacity-50 pointer-events-none" : ""
                }`}
                onClick={() => handleCardClick(list)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold dark:text-white text-slate-900 group-hover:text-sky-600 dark:group-hover:text-sky-400 transition-colors">
                        {list.name}
                      </h3>
                      {list.description && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 line-clamp-1">
                          {list.description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Menu button */}
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuId(isMenuOpen ? null : list.id);
                      }}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>

                    {/* Dropdown menu */}
                    {isMenuOpen && (
                      <div
                        className="absolute right-0 top-full mt-1 w-36 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden z-10"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => {
                            setEditingList(list);
                            setShowCreateModal(true);
                            setOpenMenuId(null);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(list.id)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Stats row */}
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                    <Users className="w-4 h-4" />
                    <span>{(list.contact_count || 0).toLocaleString()} contacts</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                    <CheckCircle className="w-4 h-4" />
                    <span>{(list.verified_count || 0).toLocaleString()} verified</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
                    <Send className="w-4 h-4" />
                    <span>{(list.sent_count || 0).toLocaleString()} sent</span>
                  </div>
                </div>

                {/* Source and last updated */}
                <div className="flex items-center justify-between mt-3">
                  {list.source && (
                    <span className="inline-flex px-2 py-0.5 text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded">
                      {DATA_SOURCES.find((s) => s.id === list.source)?.label || list.source}
                    </span>
                  )}
                  {list.updated_at && (
                    <p className="text-xs text-slate-400 dark:text-slate-500">
                      Updated {formatRelativeTime(list.updated_at)}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit modal */}
      {showCreateModal && (
        <CreateListModal
          list={editingList}
          onClose={() => {
            setShowCreateModal(false);
            setEditingList(null);
          }}
          onSaved={(savedList) => {
            if (editingList) {
              // Update existing list
              setLists((prev) =>
                prev.map((l) => (l.id === savedList.id ? savedList : l))
              );
            } else {
              // Add new list
              setLists((prev) => [savedList, ...prev]);
            }
            setShowCreateModal(false);
            setEditingList(null);
          }}
        />
      )}
    </div>
  );
};

export default MailingListsView;
